/* ============================================================
   STICKY ATC BAR — sticky-atc.js
   ============================================================ */

class StickyAtcBar extends HTMLElement {
  constructor() {
    super();

    this.alwaysShow        = this.dataset.alwaysShow === 'true';
    this.showVariantPicker = this.dataset.showVariantPicker === 'true';
    this.showQty           = this.dataset.showQty === 'true';
    this.showPriceOnBtn    = this.dataset.showPriceOnBtn === 'true';

    this.currentVariantId    = null;
    this.currentPrice        = null;
    this.currentCompareAt    = null;
    this.currentVariantTitle = null;
    this.qty                 = 1;
    this.isLoading           = false;
    this.isVisible           = false;

    this._atcInView     = true;  // assume visible on load
    this._productInView = true;  // assume visible on load
  }

  connectedCallback() {
    this.bar           = this.querySelector('.sticky-atc-bar');
    this.btn           = this.querySelector('.sticky-atc-bar__btn');
    this.btnText       = this.querySelector('.sticky-atc-bar__btn-text');
    this.btnIcon       = this.querySelector('.sticky-atc-bar__btn-icon');
    this.loader        = this.querySelector('.satc-loader');
    this.priceEl       = this.querySelector('.sticky-atc-bar__price');
    this.variantNameEl = this.querySelector('.sticky-atc-bar__variant-name');
    this.variantSelect = this.querySelector('.sticky-atc-bar__variant-select');
    this.qtyInput      = this.querySelector('.sticky-atc-bar__qty-input');
    this.qtyMinus      = this.querySelector('[data-satc-minus]');
    this.qtyPlus       = this.querySelector('[data-satc-plus]');
    this.imageEl       = this.querySelector('.sticky-atc-bar__image img');
    this.changeLink    = this.querySelector('.sticky-atc-bar__change-link');

    this._setupObserver();
    this._setupVariantSync();
    this._setupQty();
    this._setupButton();
    this._setupChangeLink();
    this._setupVariantSelect();
    this._syncInitialState();
  }

  /* ── CORE VISIBILITY ───────────────────────────────────────

     TRIGGER: Watch #Action_buttons-{id} with NO rootMargin.
     The observer fires with isIntersecting=true on load (button
     is visible). We only update _atcInView after the FIRST fire,
     then on each subsequent fire check getBoundingClientRect to
     know whether the button left through the TOP (behind the
     header) vs the bottom. Only leaving through the top shows
     the bar — this exactly matches the plugin behaviour.

     BOUNDARY: Watch the outer .main_product_wrapper-{id} div.
     When it leaves the viewport (user scrolled past the entire
     product section), hide the bar. No rootMargin — fires at
     the true viewport edge in both directions.
  ─────────────────────────────────────────────────────────── */
  _setupObserver() {
    const sectionId = this.dataset.sectionId;

    this._watchEl = document.querySelector(`#Action_buttons-${sectionId}`)
                 || document.querySelector('.main_product_buy_wrapper');
                 

    if (!this._watchEl) {
      console.warn('[StickyATC] Cannot find buy wrapper element.');
      return;
    }

    const headerPx = this._headerHeight();

    this._triggerObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          /* Button is visible — always hide the bar */
          this._atcInView = true;
        } else {
          /* Button left viewport. Only count it if it left
             through the TOP — i.e. it went behind the header.
             If it left through the bottom (page hasn't scrolled
             that far yet), ignore it. */
          const rect = entry.boundingClientRect;
          if (rect.top < headerPx) {
            this._atcInView = false;
          }
          /* If rect.top >= headerPx the button is below the
             viewport — page is scrolling up past load point,
             don't show the bar. */
        }
        this._evaluate();
      });
    }, { threshold: 0 });
    /* No rootMargin — we handle header offset via getBoundingClientRect */

    this._triggerObserver.observe(this._watchEl);

    /* BOUNDARY observer — hide when product section exits viewport */
    if (!this.alwaysShow) {
      this._productEl =
        document.querySelector(`.main_product_wrapper-${sectionId}`)
        || document.querySelector('[class*="main_product_wrapper-"]');

      if (this._productEl) {
        this._boundaryObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this._productInView = entry.isIntersecting;
            this._evaluate();
          });
        }, { threshold: 0 });

        this._boundaryObserver.observe(this._productEl);
        /* FOOTER observer — hide the sticky as soon as the footer 
   enters the viewport. The product wrapper boundary alone
   fires too late because it includes related products, 
   collapsibles, and other long content. */
this._footerEl = document.querySelector('footer') 
              || document.querySelector('.footer-group')
              || document.querySelector('[class*="footer"]');

if (this._footerEl) {
  this._footerObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this._footerInView = true;
      } else {
        this._footerInView = false;
      }
      this._evaluate();
    });
  }, { 
    threshold: 0,
    rootMargin: '0px 0px -10% 0px'  /* fire slightly before footer is technically visible */
  });
  
  this._footerObserver.observe(this._footerEl);
}
      }
    }
  }

  _headerHeight() {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height').trim();
    return parseInt(raw, 10) || 0;
  }

  _evaluate() {
  const shouldShow = !this._atcInView 
                  && this._productInView 
                  && !this._footerInView;     /* ← add this check */
  if (shouldShow) this._show();
  else this._hide();
  }

  _show() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.bar.classList.add('is-visible');
  }

  _hide() {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.bar.classList.remove('is-visible');
  }

  /* ── VARIANT SYNC ──────────────────────────────────────────
     Watch #Price-{sectionId} — stable element that updateContent()
     always updates last after the fetch. By the time it mutates,
     [data-selected-variant] is fresh in the DOM.
  ─────────────────────────────────────────────────────────── */
  _setupVariantSync() {
    const sectionId = this.dataset.sectionId;
    const priceEl   = document.querySelector(`#Price-${sectionId}`);

    if (priceEl) {
      new MutationObserver(() => this._syncVariant())
        .observe(priceEl, { childList: true, subtree: true });
    } else {
      document.addEventListener('variant-change', () => {
        setTimeout(() => this._syncVariant(), 800);
      });
    }
  }

  _syncVariant() {
    const el = document.querySelector('[data-selected-variant]');
    if (!el) return;
    let variant;
    try { variant = JSON.parse(el.textContent); } catch { return; }
    if (!variant) return;

    this.currentVariantId    = variant.id;
    this.currentPrice        = variant.price;
    this.currentCompareAt    = variant.compare_at_price;
    this.currentVariantTitle = variant.title !== 'Default Title'
      ? variant.title : null;

    this._updatePrice();
    this._updateVariantName();
    this._updateAvailability(variant);
    this._updateButtonText(variant.available);
    this._syncSelect(variant.id);
  }

  _syncInitialState() {
    const el = document.querySelector('[data-selected-variant]');
    if (el) {
      let variant;
      try { variant = JSON.parse(el.textContent); } catch {}
      if (variant) {
        this.currentVariantId    = variant.id;
        this.currentPrice        = variant.price;
        this.currentCompareAt    = variant.compare_at_price;
        this.currentVariantTitle = variant.title !== 'Default Title'
          ? variant.title : null;
        this._updatePrice();
        this._updateVariantName();
        this._updateAvailability(variant);
        this._updateButtonText(variant.available);
        this._syncSelect(variant.id);
        return;
      }
    }

    const mainBtn = document.querySelector(
      '.main_product_buy_wrapper .product_form_buy_button'
    );
    if (mainBtn) {
      const price = parseInt(mainBtn.dataset.variantPrice, 10);
      if (!isNaN(price)) {
        this.currentPrice = price;
        this._updatePrice();
        this._updateButtonText(true);
      }
    }
  }

  /* ── PRICE ──────────────────────────────────────────────── */
  _updatePrice() {
    if (!this.priceEl || this.currentPrice == null) return;
    const fmt = Shopify.money_format || '${{amount}}';
    let html  = `<span>${Shopify.formatMoney(this.currentPrice, fmt)}</span>`;
    if (this.currentCompareAt && this.currentCompareAt > this.currentPrice) {
      html += ` <s>${Shopify.formatMoney(this.currentCompareAt, fmt)}</s>`;
    }
    this.priceEl.innerHTML = html;
  }

  /* ── BUTTON TEXT ────────────────────────────────────────── */
  _updateButtonText(available = true) {
    if (!this.btnText) return;
    if (!available) {
      this.btnText.textContent = window.theme?.strings?.soldOut || 'Sold out';
      return;
    }
    const base = window.theme?.strings?.addToCart || 'Add to cart';
    if (this.showPriceOnBtn && this.currentPrice != null) {
      const fmt = Shopify.money_format || '${{amount}}';
      this.btnText.textContent = `${base} \u2014 ${Shopify.formatMoney(this.currentPrice, fmt)}`;
    } else {
      this.btnText.textContent = base;
    }
  }

  /* ── VARIANT NAME ───────────────────────────────────────── */
  _updateVariantName() {
    if (!this.variantNameEl) return;
    if (this.currentVariantTitle) {
      this.variantNameEl.textContent = this.currentVariantTitle;
      this.variantNameEl.hidden = false;
    } else {
      this.variantNameEl.hidden = true;
    }
  }

  /* ── BUTTON AVAILABILITY ────────────────────────────────── */
  _updateAvailability(variant) {
    if (!this.btn) return;
    this.btn.disabled = !variant.available;
  }

  /* ── VARIANT SELECT SYNC ────────────────────────────────── */
  _syncSelect(variantId) {
    if (this.variantSelect) this.variantSelect.value = String(variantId);
  }

  _setupVariantSelect() {
    if (!this.variantSelect) return;
    this.variantSelect.addEventListener('change', () => {
      const variantId = this.variantSelect.value;
      const variants  = window.meta?.product?.variants || [];
      const variant   = variants.find(v => String(v.id) === variantId);
      if (!variant) return;

      const picker = document.querySelector('variant-picker');
      if (!picker) return;

      const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);

      picker.querySelectorAll('select').forEach((sel, i) => {
        if (options[i] !== undefined) {
          sel.value = options[i];
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      picker.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (options.includes(radio.dataset.value)) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* ── QUANTITY ───────────────────────────────────────────── */
  _setupQty() {
    if (!this.qtyInput || !this.qtyMinus || !this.qtyPlus) return;

    this.qtyMinus.addEventListener('click', () => {
      this.qty = Math.max(1, this.qty - 1);
      this.qtyInput.value    = this.qty;
      this.qtyMinus.disabled = this.qty <= 1;
    });

    this.qtyPlus.addEventListener('click', () => {
      this.qty++;
      this.qtyInput.value    = this.qty;
      this.qtyMinus.disabled = false;
    });

    this.qtyInput.addEventListener('change', () => {
      const v = parseInt(this.qtyInput.value, 10);
      this.qty = (isNaN(v) || v < 1) ? 1 : v;
      this.qtyInput.value    = this.qty;
      this.qtyMinus.disabled = this.qty <= 1;
    });
  }

  /* ── CHANGE LINK ────────────────────────────────────────── */
  _setupChangeLink() {
    if (!this.changeLink) return;
    this.changeLink.addEventListener('click', (e) => {
      e.preventDefault();
      document.querySelector('.main_product_buy_wrapper')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /* ── ATC BUTTON ─────────────────────────────────────────── */
  _setupButton() {
    if (!this.btn) return;

    this.btn.addEventListener('click', async () => {
      if (this.isLoading || this.btn.disabled) return;
      this._setLoading(true);

      const variantId = document.querySelector(
        'main-product-section input[name="id"]'
      )?.value;

      const mainQtyInput = document.querySelector(
        'main-product-section input[name="quantity"]'
      );
      const quantity = this.showQty
        ? this.qty
        : (mainQtyInput ? parseInt(mainQtyInput.value, 10) || 1 : 1);

      if (!variantId) { this._setLoading(false); return; }

      try {
        const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method:  'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type':     'application/json'
          },
          body: JSON.stringify({ id: variantId, quantity: quantity })
        });

        const data = await res.json();

        const sections = await fetch(
          `${Shopify.routes.root}?sections=cart-drawer,cart,cart-bubble`
        ).then(r => r.json());

        document.dispatchEvent(
          new CustomEvent('cart-update', { detail: { sections }, bubbles: true })
        );

        if (!res.ok) throw data;

        document.querySelector('.cart_sidebar_open_btn')
          ?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));

      } catch (err) {
        console.error('[StickyATC] cart/add error:', err);
        /* Show the error message in the main product form and
           scroll the user up to it so they see the reason. */
        this._showAndScrollToError(err);
      } finally {
        this._setLoading(false);
      }
    });
  }

  _showAndScrollToError(err) {
    /* Mirror ProductatcForm.showError — populate and reveal
       the existing .product_add_error element in the main form,
       then scroll it into view. */
    const errorEl = document.querySelector(
      '.main_product_buy_wrapper .product_add_error'
    );

    if (errorEl) {
      let message = 'Unable to add item to cart.';

      if (err?.errors && typeof err.errors === 'object') {
        message = Object.entries(err.errors)
          .map(([field, val]) => `${field} ${val}`)
          .join('<br>');
      } else if (typeof err?.errors === 'string') {
        message = err.errors;
      } else if (typeof err?.description === 'string') {
        message = err.description;
      } else if (typeof err?.message === 'string') {
        message = err.message;
      }

      errorEl.innerHTML = message;
      errorEl.classList.remove('hidden');

      setTimeout(() => {
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      /* Fallback if error element not found */
      document.querySelector('.main_product_buy_wrapper')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  _setLoading(state) {
    this.isLoading = state;
    this.btn.classList.toggle('is-loading', state);
    this.loader?.classList.toggle('hidden', !state);
  }

  disconnectedCallback() {
    this._triggerObserver?.disconnect();
    this._boundaryObserver?.disconnect();
    this._footerObserver?.disconnect();
  }
}

customElements.define('sticky-atc-bar', StickyAtcBar);